import { MutableRefObject, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { getAdressTypeOption } from "api";
import { FEATURE_FLAGS } from "constants/featureFlags";
import useFeatureFlag from "hooks/useFeatureFlag";
import { StopAddressType } from "types/MultiStop";

export function useStopOptionalAddressTimezoneRestore(
  stopIndex: number,
  portLinkedTimezoneRef: MutableRefObject<string | null>,
) {
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const { setValue } = useFormContext();

  const addressName = useWatch({ name: `schedule.stops.${stopIndex}.address_name` });
  const stopAddressType = useWatch({ name: `schedule.stops.${stopIndex}.stop_address_type` });
  const stopAirportCode = useWatch({ name: `schedule.stops.${stopIndex}.stop_airport_code` });
  const stopAirportName = useWatch({ name: `schedule.stops.${stopIndex}.stop_airport_name` });
  const stopSeaportCode = useWatch({ name: `schedule.stops.${stopIndex}.stop_seaport_code` });
  const stopSeaportName = useWatch({ name: `schedule.stops.${stopIndex}.stop_seaport_name` });

  const previousAddressNameRef = useRef<string | undefined>(addressName);

  useEffect(() => {
    const previousAddressName = previousAddressNameRef.current;
    previousAddressNameRef.current = addressName;

    const addressWasCleared = !!previousAddressName && !addressName;
    if (!addressWasCleared) return;
    if (!isOptionalPortAddressFormEnabled) return;

    setValue(`schedule.stops.${stopIndex}.address_id`, undefined);
    setValue(`schedule.stops.${stopIndex}.address_reference_number`, "");

    if (stopAddressType !== StopAddressType.AIRPORT && stopAddressType !== StopAddressType.SEAPORT) return;

    const cachedTz = portLinkedTimezoneRef.current;
    if (cachedTz) {
      setValue(`schedule.stops.${stopIndex}.stop_timezone`, cachedTz, { shouldValidate: true });
      return;
    }

    const endpoint = stopAddressType === StopAddressType.AIRPORT ? "/common/airport" : "/common/seaport";
    const query =
      stopAddressType === StopAddressType.AIRPORT
        ? stopAirportCode || stopAirportName
        : stopSeaportCode || stopSeaportName;
    if (!query) return;

    getAdressTypeOption(endpoint, query).then((res) => {
      const linkedTimezone = res?.data?.results?.[0]?.address?.timezone;
      if (linkedTimezone) {
        setValue(`schedule.stops.${stopIndex}.stop_timezone`, linkedTimezone, { shouldValidate: true });
      }
    });
  }, [
    addressName,
    stopAddressType,
    stopAirportCode,
    stopAirportName,
    stopSeaportCode,
    stopSeaportName,
    stopIndex,
    setValue,
    portLinkedTimezoneRef,
    isOptionalPortAddressFormEnabled,
  ]);
}
